/**
 * Downloading a file that lives on another origin.
 *
 * A plain `<a download>` does not work here: the `download` attribute is
 * ignored for cross-origin URLs, so the browser navigates to the file instead
 * of saving it — which is why these buttons used to open a tab. Fetching the
 * bytes first and saving a same-origin blob URL is what actually produces a
 * download.
 */

/** Extension to assume per MIME type when the URL doesn't carry one. */
const EXTENSION_BY_TYPE: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/avif": "avif",
  "image/svg+xml": "svg",
  "application/pdf": "pdf",
};

/** Best-effort filename from the URL path, ignoring any query string. */
function filenameFromUrl(url: string): string | null {
  try {
    const { pathname } = new URL(url, window.location.origin);
    const last = pathname.split("/").filter(Boolean).pop();
    if (!last) return null;

    const decoded = decodeURIComponent(last);
    return /\.[a-z0-9]{2,5}$/i.test(decoded) ? decoded : null;
  } catch {
    return null;
  }
}

function saveBlob(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Freed on the next tick so the click has taken effect first.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

/**
 * Saves a remote file to the user's machine.
 *
 * Resolves `true` when the file was saved. Resolves `false` when the bytes
 * could not be fetched — almost always because the storage bucket doesn't send
 * CORS headers for this origin. The caller decides what to do about that; this
 * function deliberately does not fall back to opening a tab, since a silent tab
 * is the behaviour we're replacing.
 */
export type DownloadResult = "saved" | "opened-in-tab" | "failed";

export async function downloadRemoteFile(
  url: string,
  fallbackName: string
): Promise<DownloadResult> {
  try {
    const response = await fetch(url, { credentials: "omit", mode: "cors" });
    if (!response.ok) {
      console.warn(
        `[download] ${response.status} ${response.statusText} for ${url}`
      );
      return "failed";
    }

    const blob = await response.blob();
    const extension = EXTENSION_BY_TYPE[blob.type] ?? "jpg";
    const filename = filenameFromUrl(url) ?? `${fallbackName}.${extension}`;

    saveBlob(blob, filename);
    return "saved";
  } catch (error) {
    // CORS rejection — fall back to opening in a new tab so the user can
    // right-click → Save As manually.
    console.warn(`[download] CORS blocked, opening in new tab: ${url}`, error);
    window.open(url, "_blank", "noopener,noreferrer");
    return "opened-in-tab";
  }
}

/**
 * Saves several files in sequence.
 *
 * Sequential rather than parallel: browsers throttle or silently drop
 * simultaneous downloads, and a handful of proof images is not worth the risk.
 * Returns how many were saved.
 */
export async function downloadRemoteFiles(
  urls: string[],
  fallbackPrefix: string
): Promise<number> {
  let saved = 0;

  for (let i = 0; i < urls.length; i += 1) {
    const result = await downloadRemoteFile(
      urls[i],
      urls.length > 1 ? `${fallbackPrefix}-${i + 1}` : fallbackPrefix
    );
    if (result !== "failed") saved += 1;
  }

  return saved;
}
